/**
 * Wanderlust Jewelry - puts the fee lines in the bag next to the piece (paid engraving D-75, image engraving D-34,
 * paid resizing D-77). One submit handler for the whole page, however many fee blocks the product form has.
 *
 * Fee blocks (wj-engraving, wj-custom-size) register themselves in window.WJFeeProviders and implement:
 *   formId                      the product form they post to
 *   feeItems({ ref, quantity }) -> [{ id, quantity, properties }] for /cart/add.js ('' ref = fill in below)
 *   feeError?(message)          shows a message when the fees couldn't be added
 * On submit, before Horizon adds the piece: collect the items, give every text/resize fee line the same hidden
 * "_Fee ref" as the piece (a hidden input this script adds to the form), add the fees, then re-submit the form
 * so Horizon's own handler adds the piece and re-renders the bag. Image fees keep their own "_Image ref".
 * Every fee line gets "For" = piece title (+ variant). assets/wj-engrave-fee-guard.js keeps them in step.
 */
const providers = (window.WJFeeProviders ||= new Set());
const root = () => window.Shopify?.routes?.root || '/';
/** @type {HTMLFormElement | null} */
let passing = null;

/** Piece name for the "For" property: product title, plus the variant title when the product has variants. */
function pieceName(form, list) {
  const host = list.find((p) => p.dataset?.piece);
  const title = host?.dataset.piece || document.title;
  const id = /** @type {HTMLInputElement | null} */ (form.querySelector('[name="id"]'))?.value;
  let variants = {};
  try {
    variants = JSON.parse(host?.dataset.variants || '{}');
  } catch {
    variants = {};
  }
  const variant = id ? variants[id] : '';
  return variant ? `${title} (${variant})` : title;
}

/** @param {HTMLFormElement} form @param {string | null} ref */
function setRef(formId, ref) {
  let input = /** @type {HTMLInputElement | null} */ (document.querySelector(`input[data-wj-fee-ref][form="${formId}"]`));
  if (!ref) {
    input?.remove();
    return;
  }
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_Fee ref]';
    input.dataset.wjFeeRef = '';
    input.setAttribute('form', formId);
    document.body.append(input);
  }
  input.value = ref;
}

/** @param {SubmitEvent} event */
async function onSubmit(event) {
  const form = event.target;
  // Not form.id: Shopify's product form has an <input name="id"> (the variant), which shadows form.id
  const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
  if (!(form instanceof HTMLFormElement) || !formId) return;
  if (passing === form) {
    passing = null; // the re-submitted event: let Horizon's handler take it
    return;
  }
  // The registry plus whatever fee blocks are on the page right now (a re-rendered block may not have re-registered)
  const onPage = /** @type {any[]} */ ([...document.querySelectorAll('wj-engraving, wj-custom-size')]).filter((p) => typeof p.feeItems === 'function');
  const list = [...new Set([...providers, ...onPage])].filter((p) => p.isConnected && p.formId === formId);
  if (!list.length) return;

  const quantity = Number(new FormData(form).get('quantity')) || 1;
  const ref = `WJ-${Date.now().toString(36).toUpperCase()}`;
  const piece = pieceName(form, list);
  const items = list.flatMap((p) => p.feeItems({ ref, quantity }) || []);
  for (const item of items) item.properties = { For: piece, ...item.properties };
  setRef(formId, items.some((item) => item.properties['_Fee ref']) ? ref : null);
  if (!items.length) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error(String(response.status));
  } catch {
    list.forEach((p) => p.feeError?.("Sorry, we couldn't add the personalisation to your bag. Please try again."));
    setRef(formId, null);
    return;
  }
  passing = form;
  form.requestSubmit(/** @type {HTMLElement | null} */ (event.submitter) || undefined);
}

if (!window.WJFeeLinesReady) {
  window.WJFeeLinesReady = true;
  window.addEventListener('submit', onSubmit, { capture: true });
}
